'use client';

import { useAtomValue } from 'jotai';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { Button } from '@packages/shared/src/ui/Button/Button';
import type { ButtonProps } from '@packages/shared/src/ui/Button/Button';
import { CircularProgress } from '@packages/shared/src/ui/CircularProgress/CircularProgress';
import { ChangeProductInOrderableBasketDocument } from '@packages/gql/generated/shopping/ChangeProductInOrderableBasketDocument';
import type { CheckoutProductToChange } from '@packages/gql/generated/shopping/graphql';
import { useIntl } from 'react-intl';
import { selectedQuantity } from '../atoms';
import { addToBasketMessages } from '../AddToBasketButton/messages';
import { miniDVMessages } from './messages';

/* GraphQL */ `
  mutation ChangeProductInOrderableBasket(
    $locale: String!
    $productToChange: CheckoutProductToChange!
  ) {
    changeProductInOrderableBasket(
      payload: { locale: $locale, productToChange: $productToChange }
    ) {
      __typename
      ... on CheckoutChangedOrderableBasket {
        success
        orderableBasket {
          ...CheckoutOrderableBasketFragment
        }
      }
      ... on CheckoutServerFailure {
        failureMessage
      }
    }
  }
`;

export type ChangeProductInOrderableBasketButtonProps = Omit<
  ButtonProps,
  'onClick' | 'disabled'
> & {
  lineItemId: CheckoutProductToChange['lineItemId'];
  sku: NonNullable<CheckoutProductToChange['sku']>;
  onClose: () => void;
};

export const ChangeProductInOrderableBasketButton = ({
  lineItemId,
  sku,
  onClose,
  ...buttonProps
}: ChangeProductInOrderableBasketButtonProps) => {
  const { formatMessage } = useIntl();
  const { locale } = useI18n();
  const { addNotification } = useNotification();
  const quantity = useAtomValue(selectedQuantity);
  const [{ fetching }, executeChange] = useAuthMutation(ChangeProductInOrderableBasketDocument);

  const handleClick = async () => {
    const result = await executeChange({
      locale,
      productToChange: { lineItemId, sku, count: quantity },
    });

    const resultData = result.data?.changeProductInOrderableBasket;
    if (resultData?.__typename === 'CheckoutChangedOrderableBasket' && resultData.success) {
      addNotification({
        severity: 'success',
        message: formatMessage(addToBasketMessages.miniDVSuccess),
        duration: 5000,
      });
      onClose();
    } else {
      addNotification({
        severity: 'error',
        message: formatMessage(addToBasketMessages.errorMessage),
        duration: 5000,
      });
    }
  };

  return (
    <Button
      size="large"
      color="primary"
      className="w-full"
      {...buttonProps}
      onClick={handleClick}
      disabled={fetching}
    >
      {fetching ? (
        <CircularProgress className="text-color-secondary size-4" />
      ) : (
        formatMessage(miniDVMessages.confirmButton)
      )}
    </Button>
  );
};
